<template><div><h1 id="person" tabindex="-1"><a class="header-anchor" href="#person"><span>Person</span></a></h1>
<p>Person — Verwaltet Personen</p>
<h2 id="beschreibung" tabindex="-1"><a class="header-anchor" href="#beschreibung"><span>Beschreibung</span></a></h2>
<p>In dieser Ansicht werden Personen mit ihren Adressen angezeigt und bearbeitet. Mit der Umsetzung des Personenstamms sind bei der Person nur noch die &quot;beständigen&quot; Attribute wie z.B. Name, Geschlecht, Geburtsdatum verknüpft. Die Person behält diese Attribute, unabhängig davon, wo ihr Wohnsitz ist oder wo sie gerade arbeitet. Die Adressen (post. Adressen, E-Mailadressen, Telefonnummern usw.) sind in einer separaten Struktur abgelegt und mit der Person verknüpft. Eine Person muss nicht zwingend mit einer postalischen Adresse erfasst werden. Ein Kontakt kann über einen beliebigen Kanal hergestellt werden (z.B. auch per E-Mail oder Anruf).</p>
<p>Pfad: <code v-pre>Personen → Personen</code></p>
<h2 id="datenfelder-im-anzeige-modus" tabindex="-1"><a class="header-anchor" href="#datenfelder-im-anzeige-modus"><span>Datenfelder im Anzeige-Modus</span></a></h2>
<p><img src="@source/referenz/i/Person.jpg" alt="Person"></p>
<dl>
<dt>Personen-ID (ohne Label)</dt>
<dd>
<p>Personen-ID, eindeutiger Identifikator für jede einzelne Person, vom System vergeben</p>
</dd>
<dt>Attachment</dt>
<dd>
<p>Ein Icon in Form einer Büroklammer zeigt an, dass zu dieser Person mindestens 1 Attachment verknüpft ist.</p>
</dd>
<dt>Personentyp (ohne Label)</dt>
<dd>
<table>
<thead>
<tr>
<th>Typ</th>
<th>Beschreibung</th>
</tr>
</thead>
<tbody>
<tr>
<td>privat</td>
<td>Eine natürliche Person, die als Privatperson handelt.</td>
</tr>
<tr>
<td>Organisation</td>
<td>Juristische Person, z.B. eine Firma, eine Gemeinde oder eine Stiftung.</td>
</tr>
<tr>
<td>Kontakt</td>
<td>Kontaktperson zu einer Organisation.</td>
</tr>
</tbody>
</table>
</dd>
<dt>Personenstatus (ohne Label)</dt>
<dd>
<p>Zeigt den Kundenstatus einer Person an. Der Status wird online nach folgenden Kriterien evaluiert:</p>
<table>
<thead>
<tr>
<th>Code</th>
<th>Beschreibung</th>
</tr>
</thead>
<tbody>
<tr>
<td>Z</td>
<td>Zusager (z.B. Pate oder Mitglied)</td>
</tr>
<tr>
<td>S</td>
<td>Spender</td>
</tr>
<tr>
<td>K</td>
<td>Käufer</td>
</tr>
<tr>
<td>I</td>
<td>Individualmarketing</td>
</tr>
<tr>
<td>T</td>
<td>Teilnehmer</td>
</tr>
<tr>
<td>D</td>
<td>Drittzahler</td>
</tr>
</tbody>
</table>
<p>Zu den Personentypen Z,S,K gibt es &quot;selektive Sperren&quot;. Der Zweck dieser Sperre ist, dass eine Person aus dem Mailversand im entsprechendem Bereich ausgeschlossen wird:<br>Sperre Zusagen: kein Zusagenakquisition-Mail (macht zwar Sinn, z.B. wenn jemand kein Zusagen-Akquisitions-Mail möchte, wird aber in der Praxis meist mit dem Bereich &quot;Spenden&quot; abgedeckt)<br>Sperre Spenden: kein Spenden-Mail<br>Sperre Kauf: kein Katalogversand/keine Produktwerbung<br><br>Selektive Sperren werden durch grau gefärbte und durchgestrichene Kundenstati angezeigt:<div class="mediaobject"><img src="@source/referenz/i/Person_stati.png"></div></p>
</dd>
<dt>Geburtsdatum (ohne Label)</dt>
<dd>
<p>Wird angezeigt, falls erfasst.</p>
</dd>
<dt>Adressen (ohne Label)</dt>
<dd>
<p>Es wird pro Kanal die Standardadresse angezeigt. Es werden nur aktive Adressen angezeigt, sofern es sich nicht um die postalische Adresse handelt. Sollte die postalische Standardadresse mit einer Sperre versehen sein, wird sie trotzdem angezeigt, allerdings in roter Farbe und durchgestrichen</p>
</dd>
<dt>Notizen</dt>
<dd>
<p>Zeigt die Notizen an. Die als &quot;wichtig&quot; markierten zuoberst und nachfolgend die weiteren, chronologisch nach Erfassungsdatum. Die Sichtbarkeit der Notizen kann nach <a class="link" href="OrganisatorischeEinheiten.html" title="4. Organisatorische Einheiten">Organisatorischer Einheit</a> eingeschränkt sein.<br>Neue Notizen können direkt im Personendialog erfasst werden. Änderungen werden unter Attachments/Notizen (Aufruf im Kontextmenü) ausgeführt.</p>
</dd>
<dt>Personeninfo</dt>
<dd>
<p>Freier Text zur Adresse.</p>
</dd>
<dt>Umsatz</dt>
<dd>
<p>Umsatzgrafik der Umsätze, aufgeteilt nach Personenstatus</p>
</dd>
<dt>Zusagen</dt>
<dd>
<p>Auflistung der aktiven Zusagen mit Zusagetyp und Zahlungsart in Klammer.</p>
</dd>
<dt>Spenden</dt>
<dd>
<p>Spendenstatistik (selbsterklärend)</p>
</dd>
<dt>Kundenspezifischer Bereich</dt>
<dd>
<p>Das rechte Drittel des Personenframes ist ein organisationsspezifischer, frei definierbarer Bereich.</p>
</dd>
</dl>
<h2 id="datenfelder-im-erfassungs-und-anderungsmodus" tabindex="-1"><a class="header-anchor" href="#datenfelder-im-erfassungs-und-anderungsmodus"><span>Datenfelder im Erfassungs- und Änderungsmodus</span></a></h2>
<dl>
<dt>Personen-ID</dt>
<dd>Wie oben</dd>
<dt>Personentyp</dt>
<dd>Wie oben. Abhängig vom Personentyp stehen unterschiedliche Felder für die Erfassung zur Verfügung.</dd>
<dt>zu Organisation (nur bei Personentyp &quot;Kontakt&quot;)</dt>
<dd>Personen-ID der Organisation zu welcher der Kontakt verbunden ist/wird.</dd>
<dt>Sprache</dt>
<dd><a class="link" href="ft.refdata.sprachen.html" title="Sprachen">Sprachcode</a> , 2-stellig, hinter dem ersten Sprachcode kann eine Alternativsprache eingegeben werden, welche in mehrsprachigen Selekts verwendet werden kann, z.B. &quot;fd&quot;.</dd>
<dt>Geschlecht</dt>
<dd>Auswahl des Geschlechts feminin/masculin.</dd>
<dt>Anredecode</dt>
<dd>Codes gemäss Frame <a class="link" href="ft.refdata.anredecode.html" title="Anredecode">Anredecode</a> . Abhängig von der Parametrisierung der Tabelle <a class="link" href="ft.refdata.anreden.html" title="Einzelanreden">Anreden</a> ermittelt das System eine Anrede und zeigt diese an.</dd>
<dt>Titel</dt>
<dd>Codes gemäss Frame <a class="link" href="ft.refdata.titel.html" title="Titel und akademischer Grad">Titel</a> .</dd>
<dt>Geburtsdatum</dt>
<dd>Geburtsdatum, Eingabe mit 2- oder 4-stelliger Jahreszahl möglich.</dd>
<dt>Funktion</dt>
<dd>Funktion der Person. Nur für Personentypen &quot;Organisation&quot; und &quot;Kontakt&quot;.</dd>
<dt>Branche</dt>
<dd>Branche der Organisation. Nur für Personentyp &quot;Organisation&quot; und &quot;Kontakt&quot;.</dd>
<dt>Beruf</dt>
<dd>Beruf der Person.</dd>
<dt>Vorname</dt>
<dd>Feld zur Erfassung des Vornamens.</dd>
<dt>Nachname</dt>
<dd>Feld zur Erfassung des Nachnamens. In diesem Feld muss ein Eintrag stehen (Mussfeld zur Bildung der Anrede).</dd>
<dt>Organisation</dt>
<dd>Feld zur Erfassung des Firmennamens. Nur für Personentypen &quot;Organisation&quot; und &quot;Kontakt&quot;.</dd>
<dt>postalische Adresse der Organisation verwenden</dt>
<dd>Standardmässig wird bei Kontakten die Organisationsadresse zur Kontaktperson vererbt. D. h. Die Kontaktperson &quot;besitzt&quot; keine eigene Standardadresse und es wird die Standardadresse der verknüpften Organisation verwendet/angezeigt. Die Aktivierung dieser Checkbox wird bewirkt, dass die aktuelle Adresse des Kontaktes gelöscht und die Organisationsadresse vererbt wird.<br>Wird ein Feld der der vererbten postalischen Adresse bei der Kontaktperson angepasst (z.B. ein Eintrag im Feld Adresszusatz) hat diese zur Folge, dass die Vererbung aufgehoben wird und die postalische Adresse als Standard bei der Kontaktperson gespeichert wird.<br>Das Hinzufügen einer alternativen Adresse zu einem Kontakt hat ebenfalls eine Beendigung der Vererbung zur Folge. Die Organisationsadresse wird dann automatisch als Standardadresse übernommen.</dd>
<dt>Block für Erfassung der Standardadressen (ohne Label)</dt>
<dd>Hier können die Standardadressen für die Kanäle Post, Anruf und SMS erfasst und geändert werden.</dd>
<dt>weitere Adressen</dt>
<dd>Alternative Adressen für die Kanäle Post, Anruf und SMS und Standardadressen für weitere Kanäle erfasst und geändert werden.</dd>
<dt>Neue Notiz erfassen</dt>
<dd>Bereich für die Erfassung neuer Notizen, mit den Feldern &quot;wichtig&quot;, &quot;Organsiatorische Einheit&quot; und dem Textfeld für den Notizeintrag.<br>Der Notizeintrag wird zu den bestehenden Notizen hinzugefügt. Die Bearbeitung der bestehenden Notizen kann im Kontextmenü, unter &quot;Attachments und Notizen&quot; erfolgen (noch nicht installiert!). Die Felder sind die geleichen wie hier nachfolgend beschrieben.</dd>
<dt>wichtig (Notizen)</dt>
<dd>Markierung für hohe Priorität. Als &quot;wichtig&quot; markierte Notizen er-scheinen zuoberst in der Liste, in roter Schrift.</dd>
<dt>Org. Einheit zur Notiz</dt>
<dd>Mit einem Eintrag versehene Zeilen sind nur nach der OE-Berechtigung sichtbar/bearbeitbar, sowohl im Erfassungsdialog der Person, wie auch im Bearbeitungsdialog.</dd>
<dt>Org. Einheit zur Person</dt>
<dd>Erfassung einer organisatorischen Einheit als &quot;Owner&quot; der Person.</dd>
</dl>
<h3 id="vorschau" tabindex="-1"><a class="header-anchor" href="#vorschau"><span>Vorschau</span></a></h3>
<p>Bei einer postalischen Adresse kann die Vorschau geöffnet werden. Hier wird die formatierte Adresse in verschiedenen, wählbaren Formaten gezeigt. Das System zeigt auch Fehlermeldungen an, wenn eine Adresse für ein bestimmtes Format nicht richtig gezeigt werden kann. Zum Beispiel, wenn nur 4 Zeilen im Format zur Verfügung stehen und die Adresse aber wegen mehrzeiligen Organisations- oder Adress-Zusatzfeldern keinen Platz hat.</p>
<h2 id="funktionen-im-kontextmenu" tabindex="-1"><a class="header-anchor" href="#funktionen-im-kontextmenu"><span>Funktionen (im Kontextmenü)</span></a></h2>
<h3 id="filter" tabindex="-1"><a class="header-anchor" href="#filter"><span>Filter</span></a></h3>
<p>Schaltet einen markierten Personenfilter ein, womit nur noch die im Personenfilter enthaltenen Personen angezeigt werden.</p>
<h3 id="zu-ad-hoc-selektion-hinzufugen" tabindex="-1"><a class="header-anchor" href="#zu-ad-hoc-selektion-hinzufugen"><span>Zu Ad-Hoc Selektion hinzufügen</span></a></h3>
<p>Ergänzt die aktuell gezeigte Person in der Ad-Hoc Selektion.</p>
<h3 id="e-mail-senden" tabindex="-1"><a class="header-anchor" href="#e-mail-senden"><span>E-Mail senden</span></a></h3>
<p>Öffnet das E-Mailprogramm mit eingefügter E-Mail-Adresse.</p>
<h3 id="brief-schreiben-nur-unter-windows-mit-ms-office" tabindex="-1"><a class="header-anchor" href="#brief-schreiben-nur-unter-windows-mit-ms-office"><span>Brief schreiben... (nur unter Windows mit MS-Office)</span></a></h3>
<p>Startet einen Wizard zum Erfassen einer Aktion und Schreiben eines Briefs (mit eingefügter Adresse und Anrede). Der Brief wird am Schluss in der Aktion als Anhang gespeicher. Siehe <RouteLink to="/referenz/refs/ft.refproc.briefschreiben.html">Office-Integration</RouteLink>.</p>
<h3 id="anredekontrolle" tabindex="-1"><a class="header-anchor" href="#anredekontrolle"><span>Anredekontrolle</span></a></h3>
<p>Zeigt die aktuelle Anrede an. Je nach Wahl des Anredesets und im Bezug auf die Einzelanrede oder die Haushaltsanrede (z.B. in Mailings, etc.) wird diese Anrede und Briefanrede verwendet. Für die Anzeige der Haushaltsanrede sind die Beziehungen der angezeigten Person massgebend.</p>
<h3 id="aufgabe-erfassen" tabindex="-1"><a class="header-anchor" href="#aufgabe-erfassen"><span>Aufgabe erfassen</span></a></h3>
<p>Erfasst eine Aufgabe (z.B. Termin) zu der angezeigten Person.</p>
<h3 id="person-aktivieren" tabindex="-1"><a class="header-anchor" href="#person-aktivieren"><span>Person aktivieren</span></a></h3>
<p>Löst eine Personensperre wieder auf (Erfassung Aktivierungsereignis).</p>
<h3 id="person-sperren" tabindex="-1"><a class="header-anchor" href="#person-sperren"><span>Person sperren</span></a></h3>
<p>Erfasst eine Personensperre bei der aktuellen Person (Erfassung Refusé-Ereignis).</p>
<h3 id="drucken" tabindex="-1"><a class="header-anchor" href="#drucken"><span>Drucken</span></a></h3>
<p>Drucken der Informationen zur Person. Es können auch verknüpfte Daten wie Merkmale, Ereignisse, Umsätze usw. selektiert und gedruckt werden.</p>
<h3 id="mutationsschutz" tabindex="-1"><a class="header-anchor" href="#mutationsschutz"><span>Mutationsschutz</span></a></h3>
<p>Schützt (je nach Auswahl) den Namen oder alle Personen- und Adressdaten vor automatischen Änderungen (z.B. durch MatchMove).</p>
<h3 id="loschen" tabindex="-1"><a class="header-anchor" href="#loschen"><span>Löschen</span></a></h3>
<p>Das System löscht eine Person nur dann, wenn keine Verknüpfungen wie Merkmale, Ereignisse Umsätze, Buchungen usw. vorhanden sind.</p>
<h2 id="siehe-auch" tabindex="-1"><a class="header-anchor" href="#siehe-auch"><span>Siehe auch</span></a></h2>
</div></template>


